import { getListAccountSelector } from "app/modules/account-management/reducer/selectors";
import { Block } from "app/shared/components/blocks/block";
import DataTableView from "app/shared/components/data-table/data-table.web";
import EmptyBox from "app/shared/components/empty-box/empty-box";
import { QueueSpacing } from "app/shared/components/spacing";
import { Constant } from "app/shared/constant/constant";
import { ACCOUNT, ITEMDETAILSTATEMENT360 } from "app/shared/services/services-model.type";
import { spacing } from "app/shared/styles/spacing";
import { formatDateTime } from "app/shared/util/common";
import { formatNumberThousand } from "app/shared/util/format-currency";
import { useDesktopMediaQuery } from "app/shared/util/media-query";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { FlatList, StyleSheet, View, ListRenderItem } from "react-native";
import { ActivityIndicator, Button, Paragraph, Title, TouchableRipple, useTheme } from "react-native-paper";
import { useSelector } from "react-redux";
import { getErrorStatementDetailSelector, getResStatementDetailSelector, getStatusStatementDetailSelector } from "../../reducer/selectors";
import { DataStatementV2TableProps } from "../filter-table/statement-table";
import { reportTransferBackgroundHTMLString } from "./report/report-background";

interface DetailTransactionProps {
  onCloseModal: () => void;
  param: DataStatementV2TableProps;
}

export interface DetailData {
  AMOUNT?: string;
  CHANNELID?: string;
  CREDITACCNAME?: string;
  CREDITACCNO?: string;
  CREDITBANKNAME?: string;
  DATETIME?: string;
  DEBITACCNAME?: string;
  DEBITACCNO?: string;
  DEBITBANKNAME?: string;
  FTNARRATIVE?: string;
  SERVICECODE?: string;
  SERVICEDESCRIPTION?: string;
  SYSTEMID?: string;
  TOTALCHARGEAMT?: string;
  TOTALTAXAMOUNT?: string;
  TRANSREF?: string;
  CURRENCY_AMOUNT?: string;
  CURRENCY_TOTALCHARGEAMT?: string;
  FORMAT_DATE_TIME?: string;
  id?: string;
}

const DetailTransaction = (props: DetailTransactionProps) => {
  const { t } = useTranslation();
  const { colors, fonts } = useTheme();
  const { onCloseModal, param } = props;
  // open selector redux
  const statusStatementDetailSelector = useSelector(getStatusStatementDetailSelector);
  const responseStatementDetail = useSelector(getResStatementDetailSelector);
  const errorStatementDetail = useSelector(getErrorStatementDetailSelector);
  const listAccountStore = useSelector(getListAccountSelector);
  const isDesktop = useDesktopMediaQuery();

  const [listDetail, setListDetail] = React.useState<DetailData[]>([]);

  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        modalView: {
          width: "100%",
          height: 400,
          padding: spacing.huge,
          alignSelf: "center",
          backgroundColor: "white",
        },
        title: { color: colors.accent, paddingBottom: spacing.medium },
        textBold: {
          ...fonts.medium,
          color: colors.text,
          paddingVertical: 0,
          textAlign: "left",
          fontSize: spacing.medium,
          minWidth: spacing.galactic * 2,
        },
        text: { ...fonts.regular, color: colors.text, paddingVertical: 0, fontSize: spacing.medium },
        label: { flex: 1, minWidth: "30%" },
        value: { textAlign: "right" },
        content: { flex: 1, flexGrow: 1 },
        pending: { flex: 1, justifyContent: "center", alignItems: "center" },
        headerTable: { backgroundColor: colors.primary, borderWidth: 0, borderBottomWidth: 0 },
        headerTableError: { backgroundColor: colors.primary, borderWidth: 0 },
        labelHeader: { color: colors.text },
        empty: { flex: 1, justifyContent: "center", alignItems: "center" },
        button: { alignSelf: "center" },
      }),
    [colors, fonts],
  );

  const createPdfFile = React.useCallback((data: DetailData, type: string, accountIfo: ACCOUNT) => {
    const html2pdf = require("html2pdf.js");
    const fileName = type === Constant.CREDIT ? "GIấy báo có" : "Giấy báo nợ";
    const worker = html2pdf();
    worker
      // .from(reportTransferHTMLString(data, accountIfo, type))
      .from(reportTransferBackgroundHTMLString(data, accountIfo, type))
      .set({
        margin: [36, 36, 36, 36],
        filename: fileName + ".pdf",
        html2canvas: { dpi: 75, scale: 2, letterRendering: true },
        jsPDF: { unit: "pt", format: "a4", compressPDF: true, orientation: "portrait" },
      })
      .toPdf()
      .get("pdf")
      .then(() => {})
      .save();
  }, []);

  const onPressDownload = (type: string) => {
    const data: DetailData = listDetail[0];
    console.log("onPressDownload: ", data);
    const arrayFilter = listAccountStore?.ACCOUNT.filter((account) => {
      return account.ACCOUNTNO === param.ACCNO;
    });
    let accountInfo: ACCOUNT = {};
    if (arrayFilter?.length) {
      accountInfo = arrayFilter[0];
    }
    createPdfFile(data, type, accountInfo);
  };

  const handlerData = (_data?: ITEMDETAILSTATEMENT360[]) => {
    if (_data?.length) {
      _data.map((obj: DetailData, index) => {
        obj.id = String(index);
        obj.CURRENCY_AMOUNT = formatNumberThousand(obj.AMOUNT);
        obj.CURRENCY_TOTALCHARGEAMT = formatNumberThousand(obj.TOTALCHARGEAMT);
        obj.FORMAT_DATE_TIME = formatDateTime(obj.DATETIME, "YYMMDDHHmm", "DD/MM/YYYY HH:mm");
        return obj;
      });
      return _data;
    }
    return [];
  };

  React.useEffect(() => {
    setListDetail(handlerData(responseStatementDetail?.STATEMENT?.LIST_STATEMENT_DETAIL_360));
  }, [responseStatementDetail]);

  const renderItem = (label: string, value?: string, color?: string) => {
    return (
      <Block row spaced>
        <Paragraph style={[styles.textBold, styles.label]}>{label}</Paragraph>
        <Paragraph style={[styles.text, styles.value, { color: color ? color : colors.text }]}>{value}</Paragraph>
      </Block>
    );
  };

  const renderItemDetail: ListRenderItem<DetailData> = ({ item }) => (
    <TouchableRipple>
      <Block>
        {renderItem(t("ACC_NGAY_GIAO_DICH"), item?.FORMAT_DATE_TIME)}
        {renderItem(t("ACC_SO_THAM_CHIEU"), item?.TRANSREF)}
        {renderItem(
          t("ACC_SO_TIEN"),
          (param?.CREATEAMOUNT?.length && param?.CREATEAMOUNT !== "0" ? "+ " : "- ") + item?.CURRENCY_AMOUNT + " " + param.CURRENCYCODE,
          param?.CREATEAMOUNT?.length && param?.CREATEAMOUNT !== "0" ? colors.success : colors.error,
        )}
        {renderItem(t("ACC_PHI_GD"), item?.CURRENCY_TOTALCHARGEAMT + " " + param.CURRENCYCODE, colors.error)}
        {param?.CREATEAMOUNT?.length && param?.CREATEAMOUNT !== "0"
          ? renderItem(t("TRANFER_TAI_KHOAN_CHUYEN"), item?.DEBITACCNO)
          : renderItem(t("TRANFER_TAI_KHOAN_NHAN"), item?.CREDITACCNO)}
        {param?.CREATEAMOUNT?.length && param?.CREATEAMOUNT !== "0"
          ? renderItem(t("ACC_TEN_NGUOI_CHUYEN"), item?.DEBITACCNAME)
          : renderItem(t("ACC_TEN_NGUOI_NHAN"), item?.CREDITACCNAME)}
        {param?.CREATEAMOUNT?.length && param?.CREATEAMOUNT !== "0"
          ? renderItem(t("ACC_NGAN_HANG_CHUYEN"), item?.DEBITBANKNAME)
          : renderItem(t("ACC_NGAN_HANG_NHAN"), item?.CREDITBANKNAME)}
        {renderItem(t("ACC_NOI_DUNG"), item?.FTNARRATIVE)}
      </Block>
    </TouchableRipple>
  );

  const renderMobileDetail = statusStatementDetailSelector === "ok" && (
    <FlatList data={listDetail} keyExtractor={(_item) => String(_item.id)} renderItem={renderItemDetail} initialNumToRender={10} />
  );
  const renderMobileNotDetail = () => {
    let amountTitle = t("ACC_SO_TIEN_GHI_NO");
    let amount = "- " + param?.CURRENCY_DEBIT_AMOUNT + " " + (param?.CURRENCYCODE || "VND");
    let color = colors.error;
    if (param?.CREATEAMOUNT?.length && param?.CREATEAMOUNT !== "0") {
      amountTitle = t("ACC_SO_TIEN_GHI_CO");
      amount = "+ " + param?.CURRENCY_CREATE_AMOUNT + " " + (param?.CURRENCYCODE || "VND");
      color = colors.success;
    }
    return (
      statusStatementDetailSelector === "error" &&
      errorStatementDetail.code === "E41" && (
        <TouchableRipple>
          <Block>
            {renderItem(t("ACC_NGAY_GIAO_DICH"), param?.FORMAT_TRANS_DATE)}
            {renderItem(t("ACC_SO_THAM_CHIEU"), param?.SEQUENNO)}
            {renderItem(amountTitle, amount, color)}
            {renderItem(t("ACC_MO_TA"), param?.CONTENT)}
          </Block>
        </TouchableRipple>
      )
    );
  };

  const renderTableDetail = () => {
    const type = param?.CREATEAMOUNT?.length && param?.CREATEAMOUNT !== "0" ? Constant.CREDIT : Constant.DEBIT;
    return (
      statusStatementDetailSelector === "ok" && (
        <DataTableView
          header={[
            {
              name: "#",
              attr: "id",
            },
            {
              name: t("ACC_NGAY_GIAO_DICH"),
              attr: "FORMAT_DATE_TIME",
            },
            {
              name: t("ACC_SO_THAM_CHIEU"),
              attr: "TRANSREF",
            },
            {
              name: t("ACC_NOI_DUNG"),
              attr: "FTNARRATIVE",
              notCenter: true,
            },
            {
              name: t("ACC_SO_TIEN"),
              attr: "CURRENCY_AMOUNT",
              isCustomDataColumn: true,
              type: type,
            },
            {
              name: t("ACC_PHI_GD"),
              attr: "CURRENCY_TOTALCHARGEAMT",
              isCustomDataColumn: true,
              type: Constant.FEE,
            },
            {
              name: type === Constant.CREDIT ? t("TRANFER_TAI_KHOAN_CHUYEN") : t("TRANFER_TAI_KHOAN_NHAN"),
              attr: type === Constant.CREDIT ? "DEBITACCNO" : "CREDITACCNO",
            },
            {
              name: type === Constant.CREDIT ? t("ACC_TEN_NGUOI_CHUYEN") : t("ACC_TEN_NGUOI_NHAN"),
              attr: type === Constant.CREDIT ? "DEBITACCNAME" : "CREDITACCNAME",
            },
            {
              name: type === Constant.CREDIT ? t("ACC_NGAN_HANG_CHUYEN") : t("ACC_NGAN_HANG_NHAN"),
              attr: type === Constant.CREDIT ? "DEBITBANKNAME" : "CREDITBANKNAME",
            },
          ]}
          widthCell={!isDesktop ? 150 : undefined}
          headerStyle={styles.headerTable}
          labelHeaderStyle={styles.labelHeader}
          isPaginationView={false}
          dataTable={listDetail}
        />
      )
    );
  };

  const renderTableNotDetail = () => {
    const type = param?.CREATEAMOUNT?.length && param?.CREATEAMOUNT !== "0" ? Constant.CREDIT : Constant.DEBIT;
    return (
      statusStatementDetailSelector === "error" &&
      errorStatementDetail.code === "E41" && (
        <DataTableView
          header={[
            {
              name: "#",
              attr: "id",
            },
            {
              name: t("ACC_NGAY_GIAO_DICH"),
              attr: "FORMAT_TRANS_DATE",
            },
            {
              name: t("ACC_SO_THAM_CHIEU"),
              attr: "SEQUENNO",
            },
            {
              name: type === Constant.CREDIT ? t("ACC_SO_TIEN_GHI_CO") : t("ACC_SO_TIEN_GHI_NO"),
              attr: type === Constant.CREDIT ? "CURRENCY_CREATE_AMOUNT" : "CURRENCY_DEBIT_AMOUNT",
              isCustomDataColumn: true,
              type: type,
            },
            {
              name: t("ACC_MO_TA"),
              attr: "CONTENT",
            },
          ]}
          widthCell={!isDesktop ? 150 : undefined}
          headerStyle={styles.headerTableError}
          labelHeaderStyle={styles.labelHeader}
          isPaginationView={false}
          dataTable={[{ ...param }] || []}
        />
      )
    );
  };

  return (
    <View style={styles.modalView}>
      <Title style={styles.title}>{t("ACC_CHI_TIET_GIAO_DICH")}</Title>
      <View style={styles.content}>
        {statusStatementDetailSelector === "pending" && (
          <View style={styles.pending}>
            <ActivityIndicator size={24} />
          </View>
        )}

        {isDesktop ? renderTableDetail() : renderMobileDetail}
        {isDesktop ? renderTableNotDetail() : renderMobileNotDetail()}
        {statusStatementDetailSelector === "error" && errorStatementDetail.code !== "E41" && (
          <View style={styles.empty}>
            <EmptyBox title={errorStatementDetail.code} subtitle={errorStatementDetail.message} />
          </View>
        )}
      </View>
      {statusStatementDetailSelector === "ok" ? (
        <Block row middle center>
          {param?.CREATEAMOUNT?.length && param?.CREATEAMOUNT !== "0" ? (
            <Button
              onPress={() => onPressDownload(Constant.CREDIT)}
              children={t("COMMON_GIAY_BAO_CO")}
              mode="contained"
              style={styles.button}
              color={colors.accent}
            />
          ) : (
            <Button
              onPress={() => onPressDownload(Constant.DEBIT)}
              children={t("COMMON_GIAY_BAO_NO")}
              mode="contained"
              style={styles.button}
              color={colors.accent}
            />
          )}
          <QueueSpacing size="large" />
          <Button onPress={onCloseModal} children={t("COMMON_DONG")} mode="contained" style={styles.button} />
        </Block>
      ) : (
        <Block center>
          <Button onPress={onCloseModal} children={t("COMMON_DONG")} mode="contained" style={styles.button} />
        </Block>
      )}
    </View>
  );
};

export default DetailTransaction;
